import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

// Компонент для обрезки длинного текста с подсказкой
function TruncatedText({ text, maxLength = 20 }) {
  if (!text || text.length <= maxLength) {
    return <>{text}</>;
  }
  
  return (
    <span className="truncated-text-container">
      <span className="truncated-text">{text.substring(0, maxLength)}...</span>
      <span className="truncated-tooltip">{text}</span>
    </span>
  );
}

const ProfilesPage = () => {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [registrationDateRange, setRegistrationDateRange] = useState({ start: '', end: '' });
  const [registrationTemplateFilter, setRegistrationTemplateFilter] = useState(''); // '' | 'with' | 'without' | templateId
  const [draftRange, setDraftRange] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [registrationSort, setRegistrationSort] = useState('none'); // 'newest' | 'oldest'
  const [activitySort, setActivitySort] = useState('none'); // 'recent' | 'oldest'

  const API_URL = process.env.REACT_APP_API_URL || 'https://b.supchats.com';

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/templates`, { withCredentials: false }).then((res) => {
      if (res.data?.ok && Array.isArray(res.data.templates)) {
        setTemplates(res.data.templates);
      }
    }).catch(() => {});
  }, [API_URL]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/users/profiles`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.ok) {
        setUsers(response.data.users);
      }
    } catch (error) {
      setMessage('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const normalizeDate = (value) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'Не указан';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Не указан';
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toDate = (value) => {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  const orderRange = (range) => {
    if (!range) return null;
    const { from, to } = range;
    if (from && to && to < from) {
      return { from: to, to: from };
    }
    if (from && !to) {
      return { from, to: from };
    }
    if (!from && to) {
      return { from: to, to };
    }
    return range;
  };

  const getDateRangeBounds = () => {
    const start = normalizeDate(registrationDateRange.start);
    const end = normalizeDate(registrationDateRange.end);

    if (!start && !end) return null;

    if (start && !end) {
      return { start, end: start };
    }

    if (!start && end) {
      return { start: end, end };
    }

    if (start && end) {
      if (start <= end) {
        return { start, end };
      }
      return { start: end, end: start };
    }

    return null;
  };

  const filteredUsers = useMemo(() => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    const range = getDateRangeBounds();
    const tplFilter = registrationTemplateFilter;

    let result = users.filter((user) => {
      const matchesSearch =
        !lowerSearch ||
        user.email.toLowerCase().includes(lowerSearch) ||
        (user.contact_details && user.contact_details.toLowerCase().includes(lowerSearch));

      if (!matchesSearch) return false;

      if (tplFilter === 'with') {
        if (!user.registration_template) return false;
      } else if (tplFilter === 'without') {
        if (user.registration_template) return false;
      } else if (tplFilter && tplFilter !== 'all') {
        if (user.registration_template !== tplFilter) return false;
      }

      if (!range) return true;

      const registeredDate = normalizeDate(user.created_at);
      if (!registeredDate) return false;

      if (registeredDate < range.start) return false;
      if (registeredDate > range.end) return false;
      return true;
    });

    if (registrationSort !== 'none') {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        if (registrationSort === 'newest') {
          return dateB - dateA;
        }
        return dateA - dateB;
      });
    }

    if (activitySort !== 'none') {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.last_login || 0).getTime();
        const dateB = new Date(b.last_login || 0).getTime();
        if (activitySort === 'recent') {
          return dateB - dateA;
        }
        return dateA - dateB;
      });
    }

    return result;
  }, [users, searchTerm, registrationDateRange, registrationTemplateFilter, registrationSort, activitySort]);

  const handleFilterChange = (value) => {
    switch (value) {
      case 'registration_desc':
        setRegistrationSort('newest');
        setActivitySort('none');
        break;
      case 'registration_asc':
        setRegistrationSort('oldest');
        setActivitySort('none');
        break;
      case 'activity_desc':
        setActivitySort('recent');
        setRegistrationSort('none');
        break;
      case 'activity_asc':
        setActivitySort('oldest');
        setRegistrationSort('none');
        break;
      default:
        setRegistrationSort('none');
        setActivitySort('none');
    }
  };

  const currentSortValue = () => {
    if (registrationSort === 'newest') return 'registration_desc';
    if (registrationSort === 'oldest') return 'registration_asc';
    if (activitySort === 'recent') return 'activity_desc';
    if (activitySort === 'oldest') return 'activity_asc';
    return 'none';
  };

  const openCalendar = () => {
    const from = toDate(registrationDateRange.start);
    const to = toDate(registrationDateRange.end);
    const initialRange =
      from || to ? orderRange({ from: from || to, to: to || from }) : null;
    setDraftRange(initialRange);
    setCalendarOpen(true);
  };

  const closeCalendar = () => {
    setCalendarOpen(false);
  };

  const handleDraftRangeChange = (range) => {
    if (!range) {
      setDraftRange(null);
      return;
    }
    setDraftRange(orderRange(range));
  };

  const applyDraftRange = () => {
    if (!draftRange?.from) {
      setRegistrationDateRange({ start: '', end: '' });
      setCalendarOpen(false);
      return;
    }

    const start = normalizeDate(draftRange.from);
    const end = normalizeDate(draftRange.to || draftRange.from);
    setRegistrationDateRange({ start, end });
    setCalendarOpen(false);
  };

  const clearDraftRange = () => {
    setDraftRange(null);
    setRegistrationDateRange({ start: '', end: '' });
    setCalendarOpen(false);
  };


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Скопировано в буфер обмена');
    setTimeout(() => setMessage(''), 2000);
  };

  if (loading) {
    return <div className="admin-loading">Загрузка профилей...</div>;
  }

  return (
    <div className="profiles-page page-container">
      <div className="page-header-fixed">
        {message && (
          <div className={`admin-message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="search-container">
          <input
            type="text"
            placeholder="Поиск по email или контактным данным..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-results">
            Найдено: {filteredUsers.length} из {users.length}
          </span>
        </div>

        <div className="moderation-stats-compact with-filters">
          <div className="stat-item-compact">
            <span className="stat-label">Всего профилей:</span>
            <span className="stat-value">{users.length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">С контактами:</span>
            <span className="stat-value">{users.filter(user => user.contact_details).length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">Email подтвержден:</span>
            <span className="stat-value">{users.filter(user => user.email_verified).length}</span>
            </div>
          <div className="stat-item-compact">
            <span className="stat-label">С шаблоном:</span>
            <span className="stat-value">{users.filter(user => user.registration_template).length}</span>
          </div>
          <div className="stat-item-compact">
            <span className="stat-label">Активных сегодня:</span>
            <span className="stat-value">
                {users.filter(user => {
                  if (!user.last_login) return false;
                  const today = new Date();
                  const lastLogin = new Date(user.last_login);
                  return today.toDateString() === lastLogin.toDateString();
                }).length}
              </span>
          </div>
          <div className="filters-group">
            <div className="filter-select-wrapper">
              <label htmlFor="template-filter">Шаблон:</label>
              <select
                id="template-filter"
                value={registrationTemplateFilter}
                onChange={(e) => setRegistrationTemplateFilter(e.target.value)}
              >
                <option value="">Все</option>
                <option value="with">С шаблоном</option>
                <option value="without">Без шаблона</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title_ru || t.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-select-wrapper">
              <label htmlFor="combined-filter">Фильтр:</label>
              <select
                id="combined-filter"
                value={currentSortValue()}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                <option value="none">Без сортировки</option>
                <option value="registration_desc">Дата регистрации ↓</option>
                <option value="registration_asc">Дата регистрации ↑</option>
                <option value="activity_desc">Активность ↓</option>
                <option value="activity_asc">Активность ↑</option>
              </select>
            </div>

            <div className="date-range-filter inline">
              <label>Период регистрации:</label>
              <button
                type="button"
                className="date-picker-toggle"
                onClick={() => (calendarOpen ? closeCalendar() : openCalendar())}
              >
                {registrationDateRange.start
                  ? registrationDateRange.end &&
                    registrationDateRange.end !== registrationDateRange.start
                    ? `${registrationDateRange.start} — ${registrationDateRange.end}`
                    : registrationDateRange.start
                  : 'Выбрать период'}
              </button>
              {registrationDateRange.start && (
                <button
                  type="button"
                  className="clear-filter-btn inline"
                  onClick={() => {
                    setDraftRange(null);
                    setRegistrationDateRange({ start: '', end: '' });
                    closeCalendar();
                  }}
                  title="Очистить период"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-scrollable">
        <div className="profiles-grid">
        {filteredUsers.map(user => (
          <div key={user.id} className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </div>
              <div className="profile-info">
                <h3>{user.name || 'Не указано'}</h3>
                <p className="profile-email">{user.email}</p>
                <small className="profile-id">ID: {user.id}</small>
              </div>
            </div>

            <div className="profile-details">
              <div className="detail-row">
                <label>Дата регистрации:</label>
                <span>{formatDateTime(user.created_at)}</span>
              </div>

              <div className="detail-row">
                <label>Последний вход:</label>
                <span>{user.last_login ? formatDateTime(user.last_login) : 'Никогда'}</span>
              </div>

              <div className="detail-row">
                <label>Email подтвержден:</label>
                <span className={user.email_verified ? 'verified' : 'not-verified'}>
                  {user.email_verified ? '✅ Да' : '❌ Нет'}
                </span>
              </div>

              <div className="detail-row">
                <label>Статус аккаунта:</label>
                <span className={user.is_active ? 'active' : 'blocked'}>
                  {user.is_active ? '✅ Активен' : '🚫 Заблокирован'}
                </span>
              </div>

              {user.registration_template && (
                <div className="detail-row">
                  <label>Шаблон при регистрации:</label>
                  <span>{user.registration_template}</span>
                </div>
              )}
            </div>

            <div className="contact-section">
              <label>Обратная связь:</label>
              {user.contact_details ? (
                <div className="contact-details">
                  <p>{user.contact_details}</p>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(user.contact_details)}
                    title="Копировать контактные данные"
                  >
                    📋 Копировать
                  </button>
                </div>
              ) : (
                <p className="no-contact">Не указана</p>
              )}
            </div>

            <div className="agents-section">
              <label>Боты пользователя:</label>
              {user.agents?.length > 0 ? (
                <div className="user-agents">
                  {user.agents.map(agent => (
                    <div key={agent.id} className="agent-badge">
                      <span className="agent-name">
                        <TruncatedText text={agent.name} maxLength={15} />
                      </span>
                      <span className={`agent-status ${agent.is_active ? 'active' : 'blocked'}`}>
                        {agent.is_active ? '✅' : '🚫'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-agents">Нет ботов</p>
              )}
            </div>

            <div className="profile-actions">
              <button
                className="contact-btn"
                onClick={() => copyToClipboard(user.email)}
                title="Копировать email"
              >
                📧 Email
              </button>
              {user.contact_details && (
                <button
                  className="contact-btn"
                  onClick={() => copyToClipboard(user.contact_details)}
                  title="Копировать контакты"
                >
                  📞 Контакты
                </button>
              )}
            </div>
          </div>
        ))}
        </div>

        {filteredUsers.length === 0 && !loading && (
          <div className="no-results">
            <p>Пользователи не найдены</p>
            {searchTerm && (
              <button 
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
              >
                Очистить поиск
              </button>
            )}
          </div>
        )}
      </div>
      {calendarOpen && (
        <div className="calendar-floating" onClick={closeCalendar}>
          <div className="calendar-floating-inner" onClick={(e) => e.stopPropagation()}>
            <DayPicker
              mode="range"
              selected={draftRange ?? undefined}
              onSelect={handleDraftRangeChange}
              defaultMonth={draftRange?.from || draftRange?.to || new Date()}
              weekStartsOn={1}
              captionLayout="dropdown-buttons"
              footer={
                draftRange?.from
                  ? `Выбран период: ${normalizeDate(draftRange.from)}${
                      draftRange.to && normalizeDate(draftRange.to) !== normalizeDate(draftRange.from)
                        ? ` — ${normalizeDate(draftRange.to)}`
                        : ''
                    }`
                  : 'Выберите дату или диапазон.'
              }
            />
            <div className="calendar-actions">
              <button
                type="button"
                className="apply-filter-btn inline"
                onClick={applyDraftRange}
                disabled={!draftRange?.from}
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilesPage;

